import { useTranslation } from 'react-i18next'
import { Select } from '../ui/Select'
import type { RepoRef } from './useRepos'

/**
 * Repository selector for a chat (docs/connectors.md §10). Presentational on purpose: the caller
 * owns the RPC, so the picker can be rendered without a server and the same control serves the
 * repository dock of a chat and the new-chat screen.
 *
 * The trigger is borderless: the control always sits in a dock or a composer bar, inside the card
 * that holds the input, where a boxed select would read as a second card.
 *
 * Renders nothing while the workspace has no repositories — there is nothing to choose, and an
 * empty control would only take space in the dock.
 */
export function RepoPicker({ repos, value, onChange, id, disabled }: {
  repos: RepoRef[]
  value: string | null
  onChange(repoId: string | null): void
  id?: string
  disabled?: boolean
}) {
  const { t } = useTranslation()
  if (repos.length === 0) return null
  const name = (r: RepoRef) => `${r.owner}/${r.name}${r.readOnly ? ` (${t('repos.readOnly')})` : ''}`
  const selected = repos.find(r => r.id === value)
  // The accessible name repeats the visible value (WCAG 2.5.3): the trigger shows "owner/name", so
  // a name of only "Kho mã" would not match what a voice-control user reads on screen.
  return (
    <Select
      id={id} label={`${t('repos.picker')}: ${selected ? name(selected) : t('repos.none')}`}
      variant="inline"
      className="min-w-0 max-w-[8rem] sm:max-w-[16rem]" disabled={disabled}
      value={value ?? ''} onChange={v => onChange(v || null)}
      options={[
        { value: '', label: t('repos.none') },
        ...repos.map(r => ({ value: r.id, label: name(r), hint: r.defaultBranch }))
      ]}
    />
  )
}
